import React, { useEffect, useRef, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import Topbar from "./Topbar";
import Sidebar from "./Sidebar";
import { jwtDecode } from "jwt-decode";
import { requestFirebaseToken } from "../lib/firebaseUtil";

import { createContext } from "react";
import { getMessaging, onMessage } from "firebase/messaging";
import { useDispatch } from "react-redux";
import { clearToken } from "../redux/slices/authSlice";

export const NotificationContext = createContext<any>(null);
const ProtectedRoute: React.FC = () => {
  const [notification, setNotification] = useState<any>({});
  const [showAlertOnRouteChange, setShowAlertOnRouteChange] = useState(false);
  const [showAlertAction, setShowAlertAction] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const lastToken = useRef<any>(null);

  const [navigateToLogin, setNavigateToLogin] = useState<string>("");
  const token = useSelector((state: RootState) => state.auth.token);
  const dispatch = useDispatch();
  useEffect(() => {
    if (token) {
      let decodedToken;
      try {
        decodedToken = jwtDecode<{ role: string }>(token);
        if (decodedToken) {
          const tokenRole = decodedToken.role;
          setNavigateToLogin(
            tokenRole === "VENDOR" ? "/vendor-login" : "/admin-login"
          );
        }
      } catch (error) {
        console.error("Token decoding error:", error);
      }
    }
  }, [token]);

  const messaging = getMessaging();
  useEffect(() => {
    const handleIncomingMessage = (payload: any) => {
      handleNotificationReceived(payload);
    };

    const unsubscribe = onMessage(messaging, handleIncomingMessage);

    const channel = new BroadcastChannel("firebaseNotifications");
    channel.onmessage = (event) => {
      const newNotification = event.data;
      handleNotificationReceived(newNotification);
    };

    return () => {
      unsubscribe();
      channel.close();
    };
  }, [messaging]);

  const handleLogout = () => {
    dispatch(clearToken());
  };

  if (!token) {
    return <Navigate to={navigateToLogin} replace />;
  } else {
    const decodedToken = jwtDecode<{ id: number }>(token);
    if (lastToken.current !== token) {
      lastToken.current = token;
      requestFirebaseToken(decodedToken.id, token);
    }
  }

  const saveNotification = (message: any) => {
    const decodedToken = jwtDecode<{ id: number; role: string }>(token);
    message.data.timestamp = new Date().toISOString();
    let notifications: any = localStorage.getItem(
      `${decodedToken.role}_${decodedToken.id}_notifications`
    );
    if (notifications) {
      notifications = JSON.parse(notifications);
    } else {
      notifications = [];
    }

    notifications.push(message);
    localStorage.setItem(
      `${decodedToken.role}_${decodedToken.id}_notifications`,
      JSON.stringify(notifications)
    );
  };

  const handleNotificationReceived = (payload: any) => {
    if (payload) {
      saveNotification(payload);
      setNotification(payload);
    }
  };

  return (
    <>
      <NotificationContext.Provider value={notification}>
        <div className="flex w-full h-screen overflow-hidden select-none bg-bgColor">
          <div
            className={`w-44 text-white absolute md:relative md:block ${
              isSidebarOpen ? "block" : "hidden"
            } z-50`}
          >
            <Sidebar
              showAlertOnRouteChange={showAlertOnRouteChange}
              setShowAlertOnRouteChange={setShowAlertOnRouteChange}
              isSidebarOpen={isSidebarOpen}
              setIsSidebarOpen={setIsSidebarOpen}
              setShowAlertAction={setShowAlertAction}
            />
          </div>
          <div className="flex-1 w-full overflow-y-auto">
            <div className="flex-1 w-full sticky top-0 bg-white z-40">
              <Topbar
                handleLogout={handleLogout}
                isSidebarOpen={isSidebarOpen}
                setIsSidebarOpen={setIsSidebarOpen}
              />
            </div>
            <Outlet
              context={{
                setShowAlertOnRouteChange,
                showAlertOnRouteChange,
                showAlertAction,
                setShowAlertAction,
              }}
            />
          </div>
        </div>
      </NotificationContext.Provider>
    </>
  );
};

export default ProtectedRoute;
